import * as XLSX from 'xlsx'
import { countryNameToIso2 } from '../lib/cds/countries'
import type { InvoicePivotItem, InvoiceHeader } from './claudeInvoiceParser'

export function generateCDSExcel(
  header: InvoiceHeader,
  pivot: InvoicePivotItem[],
  useCyukDan: boolean = false
): void {
  if (!pivot || pivot.length === 0) {
    alert('Cannot export: no items in invoice pivot table')
    return
  }

  // ORIGIN CNTRY must be an ISO-2 code. Older saved invoices may still hold AI text like
  // "Country of Origin: china" -- extract the code, and refuse to export text we can't resolve.
  const rawOrigin = header.country_of_origin?.trim() || ''
  const originIso = rawOrigin ? countryNameToIso2(rawOrigin) : 'CN'
  if (!originIso) {
    alert(`Cannot export: Country of Origin "${rawOrigin}" isn't a recognised country. Correct it to a 2-letter code (e.g. CN) and export again.`)
    return
  }

  const invoiceNo = header.invoice_no || 'INVOICE'
  const currency = header.currency || 'USD'

  // Common columns for all sheets
  const commonColumns = {
    'GOODS DESCRIPTION': '',
    'GROSS MASS': 0,
    'NET MASS': 0,
    'VALUE': 0,
    'VALUE CURR': currency,
    'COMMODITY CODE': '',
    'DOC REF 1': invoiceNo,
    'DOC REASON 1': null,
    'PREV DOC REF 1': invoiceNo,
    'PKG QTY': 0,
    'PKG TYPE': 'CT',
    'PKG MARKS': 'N/M',
    'SUPP UNITS': 0,
    'SUPP UNITS CODE': null,
    'VALUATION METHOD': 1,
    'CPC FIRST 4': 4000,
    'CPC ADDITIONAL': '000',
    'ORIGIN CNTRY': originIso,
    'PREFERENCE': 100,
  }

  // CYUK DAN sheet columns (includes all common + fixed columns)
  const cyukDanColumns = {
    ...commonColumns,
    'DOC CODE 1': 'N935',
    'DOC STATUS 1': 'AC',
    'DOC CODE 2': 'C505',
    'DOC STATUS 2': 'CC',
    'DOC REF 2': 'GBCGUGUARANTEENOTREQUIRED',
    'DOC CODE 3': 'C506',
    'DOC STATUS 3': null,
    'DOC REF 3': 'GBDPO6150757',
    'PREV DOC CLASS 1': 'Z',
    'PREV DOC TYPE 1': 380,
    'TAX TYPE 1': 'A00',
    'TAX MOP 1': 'E',
    'TAX TYPE 2': 'B00',
    'TAX MOP 2': 'E',
  }

  // NO CYUK DAN sheet columns (common columns only, 19 columns total)
  const noCyukDanColumns = {
    ...commonColumns,
    // No extra document codes for NO CYUK DAN
  }

  // Create data rows for each sheet
  // Always populate both sheets with data based on useCyukDan setting
  const cyukDanRows = pivot.map(item => ({
    ...cyukDanColumns,
    'GOODS DESCRIPTION': item.goods_description,
    'GROSS MASS': item.gross_weight,
    'NET MASS': item.net_weight,
    'VALUE': item.amount,
    'COMMODITY CODE': item.hs_code,
    'PKG QTY': item.ctns,
    'SUPP UNITS': item.pcs,
  }))

  const noCyukDanRows = pivot.map(item => ({
    ...noCyukDanColumns,
    'GOODS DESCRIPTION': item.goods_description,
    'GROSS MASS': item.gross_weight,
    'NET MASS': item.net_weight,
    'VALUE': item.amount,
    'COMMODITY CODE': item.hs_code,
    'PKG QTY': item.ctns,
    'SUPP UNITS': item.pcs,
  }))

  const wb = XLSX.utils.book_new()

  // Only one of these ever has data -- when useCyukDan is true, USE CYUK DAN
  // gets it and NO CYUK DAN is left as an empty (header-only) sheet, and vice
  // versa. The empty sheet is kept (not omitted) since downstream consumers
  // may expect both tab names to always exist.
  const cyukDanRowsToUse = useCyukDan ? cyukDanRows : []
  const noCyukDanRowsToUse = !useCyukDan ? noCyukDanRows : []

  const cyukDanWs = XLSX.utils.json_to_sheet(cyukDanRowsToUse, { header: Object.keys(cyukDanColumns) })
  if (cyukDanRowsToUse.length === 0) {
    cyukDanWs['!cols'] = Object.keys(cyukDanColumns).map(k => ({ wch: Math.max(k.length, 12) }))
  }

  const noCyukDanWs = XLSX.utils.json_to_sheet(noCyukDanRowsToUse, { header: Object.keys(noCyukDanColumns) })
  if (noCyukDanRowsToUse.length === 0) {
    noCyukDanWs['!cols'] = Object.keys(noCyukDanColumns).map(k => ({ wch: Math.max(k.length, 12) }))
  }

  // Bug: USE CYUK DAN was always added first (sheet position 0) regardless
  // of useCyukDan. This library version (xlsx 0.18.5, confirmed against its
  // own WBView type -- it only supports RTL, no active-tab property at all)
  // can't pin which tab a viewer opens to, so whichever sheet a given
  // viewer defaults to showing (many fall back to alphabetical order, where
  // "NO..." sorts before "USE...") is what staff see first. When useCyukDan
  // is true, that's exactly the always-empty NO CYUK DAN sheet -- looking
  // exactly like "the export is broken", even though USE CYUK DAN (position
  // 2) has the real data. Fix: always add the POPULATED sheet first, so
  // position-based defaults land on real data regardless of setting.
  if (useCyukDan) {
    XLSX.utils.book_append_sheet(wb, cyukDanWs, 'USE CYUK DAN')
    XLSX.utils.book_append_sheet(wb, noCyukDanWs, 'NO CYUK DAN')
  } else {
    XLSX.utils.book_append_sheet(wb, noCyukDanWs, 'NO CYUK DAN')
    XLSX.utils.book_append_sheet(wb, cyukDanWs, 'USE CYUK DAN')
  }

  XLSX.writeFile(wb, `CDS_${invoiceNo}_${new Date().toISOString().slice(0,10)}.xlsx`)
}
